import type { Action } from 'redux';

import type { GetApiB2bAccountUserCurrentResponse } from '/api';
import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';

export type AuthUser = GetApiB2bAccountUserCurrentResponse;
export type AuthAccount = AuthUser['account'];

export interface AuthState {
  fetching: boolean;
  fetched: boolean;
  isAuthed: boolean;
  dismissingNotification: boolean;
  user: AuthUser | null;
  session: { token: string; expires: string } | null;
  error?: NormalisedError | null;
}

interface DismissNotificationStartedAction extends Action {
  type: 'DISMISS_NOTIFICATION_STARTED';
}

interface DismissNotificationRejectedAction extends Action {
  type: 'DISMISS_NOTIFICATION_REJECTED';
}

interface DismissNotificationFulfilledAction extends Action {
  type: 'DISMISS_NOTIFICATION_FULFILLED';
  deletedNotificationId: number;
}

interface GetCountriesRejectedAction extends Action {
  type: 'GET_COUNTRIES_REJECTED';
}

interface GetSessionStartedAction extends Action {
  type: 'GET_SESSION_STARTED';
}

interface GetSessionFulfilledAction extends Action {
  type: 'GET_SESSION_FULFILLED';
  token: string;
  expires: string;
}

interface GetSessionRejectedAction extends Action {
  type: 'GET_SESSION_REJECTED';
  error: NormalisedError;
}

interface ClearSessionAction extends Action {
  type: 'CLEAR_SESSION';
}

interface ClearAuthenticationAction extends Action {
  type: 'CLEAR_AUTHENTICATION';
}

interface FetchAuthStartedAction extends Action {
  type: 'FETCH_AUTH_STARTED';
  silent?: boolean;
}

interface FetchAuthFulfilledAction extends Action {
  type: 'FETCH_AUTH_FULFILLED';
  user?: AuthUser;
}

interface EditUserInfoFulfilledAction extends Action {
  type: 'EDIT_USER_INFO_FULFILLED';
  user: AuthUser;
}

interface FetchAuthRejectedAction extends Action {
  type: 'FETCH_AUTH_REJECTED';
  error: NormaliseErrorInput;
}

interface RegisterUserStartedAction extends Action {
  type: 'REGISTER_USER_STARTED';
}

interface RegisterUserFulfilledAction extends Action {
  type: 'REGISTER_USER_FULFILLED';
}

interface RegisterUserRejectedAction extends Action {
  type: 'REGISTER_USER_REJECTED';
  error: NormaliseErrorInput;
}

type AuthAction =
  | DismissNotificationStartedAction
  | DismissNotificationRejectedAction
  | DismissNotificationFulfilledAction
  | GetCountriesRejectedAction
  | GetSessionStartedAction
  | GetSessionFulfilledAction
  | GetSessionRejectedAction
  | ClearSessionAction
  | ClearAuthenticationAction
  | FetchAuthStartedAction
  | FetchAuthFulfilledAction
  | EditUserInfoFulfilledAction
  | FetchAuthRejectedAction
  | RegisterUserStartedAction
  | RegisterUserFulfilledAction
  | RegisterUserRejectedAction;

const defaultState: AuthState = {
  fetching: false,
  fetched: false,
  isAuthed: false,
  dismissingNotification: false,
  user: null,
  session: null,
};

function reducer(state: AuthState = defaultState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'DISMISS_NOTIFICATION_STARTED': {
      return {
        ...state,
        dismissingNotification: true,
      };
    }
    case 'DISMISS_NOTIFICATION_REJECTED': {
      return {
        ...state,
        dismissingNotification: false,
      };
    }
    case 'DISMISS_NOTIFICATION_FULFILLED': {
      const { deletedNotificationId } = action;
      const accountNotifications = state.user?.account.notifications;
      if (!state.user || !accountNotifications || accountNotifications.length === 0) {
        return {
          ...state,
          dismissingNotification: false,
        };
      }
      const newNotifications = accountNotifications.filter((item) => item.id !== deletedNotificationId);
      const user = {
        ...state.user,
        account: {
          ...state.user.account,
          notifications: newNotifications,
        },
      };
      return {
        ...state,
        user,
      };
    }
    case 'GET_COUNTRIES_REJECTED': {
      if (state.user === null && state.session !== null) {
        return {
          ...state,
          ...defaultState,
        };
      }
      return {
        ...state,
      };
    }
    case 'GET_SESSION_STARTED': {
      return {
        ...state,
        fetching: true,
      };
    }
    case 'GET_SESSION_FULFILLED': {
      const { token, expires } = action;
      return {
        ...state,
        fetching: false,
        error: null,
        session: {
          token,
          expires,
        },
      };
    }
    case 'GET_SESSION_REJECTED': {
      return {
        ...state,
        fetching: false,
        error: action.error,
      };
    }
    case 'CLEAR_SESSION': {
      return {
        ...state,
        session: null,
      };
    }
    case 'CLEAR_AUTHENTICATION': {
      return {
        ...state,
        ...defaultState,
      };
    }
    case 'FETCH_AUTH_STARTED': {
      const { silent = false } = action;
      if (silent) {
        return state;
      }
      return {
        ...state,
        fetching: true,
      };
    }
    case 'FETCH_AUTH_FULFILLED': {
      if (!action.user) {
        return state;
      }
      return {
        ...state,
        fetching: false,
        fetched: true,
        isAuthed: true,
        user: action.user,
      };
    }
    case 'EDIT_USER_INFO_FULFILLED': {
      return {
        ...state,
        fetching: false,
        fetched: true,
        isAuthed: true,
        user: action.user,
      };
    }
    case 'FETCH_AUTH_REJECTED': {
      return {
        ...state,
        fetching: false,
        fetched: true,
        isAuthed: false,
        user: null,
        error: normaliseError(action.error),
      };
    }
    case 'REGISTER_USER_STARTED': {
      return {
        ...state,
        fetching: true,
      };
    }
    case 'REGISTER_USER_FULFILLED': {
      return {
        ...state,
        fetching: false,
      };
    }
    case 'REGISTER_USER_REJECTED': {
      return {
        ...state,
        fetching: false,
        error: normaliseError(action.error),
      };
    }
    default:
      return state;
  }
}

export default reducer;
